import { useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { cn } from '@/lib/utils'
import { useAuth } from '@/hooks/useAuth'
import { useIsAdmin } from '@/hooks/useAdmin'
import {
  LogOut,
  X,
  Menu,
  LayoutDashboard,
  User,
  Building2,
  BookOpen,
  Download,
  MessageCircle,
  Shield,
  BarChart3,
  Calculator,
  Tv,
  ExternalLink,
  type LucideIcon
} from 'lucide-react'

type NavItem = {
  label: string
  href: string
  icon?: LucideIcon
  external?: boolean
}

const mainNavItems: NavItem[] = [
  { label: 'Overview', href: '/', icon: LayoutDashboard },
  { label: 'Profile', href: '/profile', icon: User },
  { label: 'Organization', href: '/organization', icon: Building2 },
]

const secondaryItems: NavItem[] = [
  {
    label: 'Docs',
    href: 'https://docs.theseus.us/cyclops/getting-started',
    icon: BookOpen,
    external: true,
  },
  { label: 'Downloads', href: '/downloads', icon: Download },
]

const adminItems: NavItem[] = [
  { label: 'Admin', href: '/admin', icon: Shield },
  { label: 'Stats', href: '/admin/stats', icon: BarChart3 },
  { label: 'Pricing', href: '/admin/pricing', icon: Calculator },
  { label: 'Office TV', href: '/admin/office-tv', icon: Tv },
]

function NavLink({ item }: { item: NavItem }) {
  const location = useLocation()
  const isActive = location.pathname === item.href
  const Icon = item.icon

  const className = cn(
    'flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-sm transition-all duration-150',
    isActive
      ? 'text-foreground bg-white/[0.05] font-medium'
      : 'text-muted-foreground/70 hover:text-foreground hover:bg-white/[0.03]'
  )

  const content = (
    <>
      {Icon && <Icon className={cn('h-4 w-4 flex-shrink-0', isActive ? 'text-primary' : '')} />}
      <span className="flex-1">{item.label}</span>
      {item.external && <ExternalLink className="h-3 w-3 text-muted-foreground/40" />}
    </>
  )

  if (item.external) {
    return (
      <a
        href={item.href}
        target="_blank"
        rel="noopener noreferrer"
        className={className}
      >
        {content}
      </a>
    )
  }

  return (
    <Link to={item.href} className={className}>
      {content}
    </Link>
  )
}

function MobileNavLink({ item, onClick }: { item: NavItem; onClick: () => void }) {
  const location = useLocation()
  const isActive = location.pathname === item.href
  const Icon = item.icon

  const className = cn(
    'flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all duration-150',
    isActive
      ? 'text-foreground bg-white/[0.05] font-medium'
      : 'text-muted-foreground/70 hover:text-foreground hover:bg-white/[0.03]'
  )

  const content = (
    <>
      {Icon && <Icon className={cn('h-4 w-4 flex-shrink-0', isActive ? 'text-primary' : '')} />}
      <span className="flex-1">{item.label}</span>
      {item.external && <ExternalLink className="h-3 w-3 text-muted-foreground/40" />}
    </>
  )

  if (item.external) {
    return (
      <a
        href={item.href}
        target="_blank"
        rel="noopener noreferrer"
        className={className}
        onClick={onClick}
      >
        {content}
      </a>
    )
  }

  return (
    <Link to={item.href} className={className} onClick={onClick}>
      {content}
    </Link>
  )
}

export function Sidebar() {
  const { signOut, getUserMetadata, user } = useAuth()
  const { isAdmin } = useIsAdmin()
  const metadata = getUserMetadata()
  const [modalState, setModalState] = useState<'closed' | 'open' | 'closing'>('closed')
  const [mobileMenuState, setMobileMenuState] = useState<'closed' | 'open' | 'closing'>('closed')

  const openModal = () => setModalState('open')
  const closeModal = () => {
    setModalState('closing')
  }

  const openMobileMenu = () => setMobileMenuState('open')
  const closeMobileMenu = () => {
    setMobileMenuState('closing')
  }

  useEffect(() => {
    if (modalState === 'closing') {
      const timer = setTimeout(() => setModalState('closed'), 150)
      return () => clearTimeout(timer)
    }
  }, [modalState])

  useEffect(() => {
    if (mobileMenuState === 'closing') {
      const timer = setTimeout(() => setMobileMenuState('closed'), 150)
      return () => clearTimeout(timer)
    }
  }, [mobileMenuState])

  // Close on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (modalState === 'open') closeModal()
        if (mobileMenuState === 'open') closeMobileMenu()
      }
    }
    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [modalState, mobileMenuState])

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuState === 'open') {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [mobileMenuState])

  const sidebarContent = (
    <>
      {/* User Info */}
      <div className="mb-6 px-2.5">
        <p className="text-sm font-medium text-foreground tracking-tight">
          {metadata.full_name || 'User'}
        </p>
        <p className="text-xs text-muted-foreground/60 mt-0.5 truncate">
          {user?.email}
        </p>
      </div>

      {/* Navigation */}
      <nav className="space-y-0.5">
        {mainNavItems.map((item) => (
          <NavLink key={item.href} item={item} />
        ))}
      </nav>

      {/* Secondary Links */}
      <div className="mt-6 pt-6 border-t border-white/[0.06] space-y-0.5">
        <p className="px-2.5 mb-2 text-[10px] uppercase tracking-wider text-muted-foreground/40 font-medium">
          Resources
        </p>
        {secondaryItems.map((item) => (
          <NavLink key={item.href} item={item} />
        ))}
        <button
          onClick={openModal}
          className="flex items-center gap-2.5 w-full px-2.5 py-2 rounded-lg text-sm text-muted-foreground/70 hover:text-foreground hover:bg-white/[0.03] transition-all duration-150"
        >
          <MessageCircle className="h-4 w-4" />
          <span>Contact Us</span>
        </button>
      </div>

      {/* Admin Links (conditional) */}
      {isAdmin && (
        <div className="mt-6 pt-6 border-t border-white/[0.06] space-y-0.5">
          <p className="px-2.5 mb-2 text-[10px] uppercase tracking-wider text-muted-foreground/40 font-medium">
            Admin
          </p>
          {adminItems.map((item) => (
            <NavLink key={item.href} item={item} />
          ))}
        </div>
      )}

      {/* Sign Out */}
      <div className="mt-6 pt-6 border-t border-white/[0.06]">
        <button
          onClick={signOut}
          className="flex items-center gap-2.5 w-full px-2.5 py-2 rounded-lg text-sm text-muted-foreground/70 hover:text-destructive hover:bg-destructive/5 transition-all duration-150"
        >
          <LogOut className="h-4 w-4" />
          <span>Sign out</span>
        </button>
      </div>
    </>
  )

  return (
    <>
      {/* Mobile Menu Button */}
      <button
        onClick={openMobileMenu}
        className="fixed top-5 right-4 z-40 p-2 md:hidden text-muted-foreground hover:text-foreground transition-colors"
        aria-label="Open menu"
      >
        <Menu className="h-5 w-5" />
      </button>

      {/* Desktop Sidebar */}
      <aside className="hidden md:block w-48 py-8 pr-6 flex-shrink-0">
        {sidebarContent}
      </aside>

      {/* Mobile Menu Drawer */}
      {mobileMenuState !== 'closed' && (
        <div
          className={cn(
            'fixed inset-0 z-50 md:hidden bg-black/70 backdrop-blur-sm',
            mobileMenuState === 'open' ? 'modal-backdrop-enter' : 'modal-backdrop-exit'
          )}
          onClick={closeMobileMenu}
        >
          <div
            className={cn(
              'absolute right-0 top-0 h-full w-64 bg-background border-l border-border/50 p-6',
              mobileMenuState === 'open' ? 'animate-slide-in-right' : 'animate-slide-out-right'
            )}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={closeMobileMenu}
              className="absolute top-4 right-4 p-2 text-muted-foreground hover:text-foreground transition-colors"
              aria-label="Close menu"
            >
              <X className="h-5 w-5" />
            </button>

            <div className="pt-8">
              {/* User Info */}
              <div className="mb-6 px-3">
                <p className="text-sm font-medium text-foreground tracking-tight">
                  {metadata.full_name || 'User'}
                </p>
                <p className="text-xs text-muted-foreground/60 mt-0.5 truncate">
                  {user?.email}
                </p>
              </div>

              {/* Navigation */}
              <nav className="space-y-0.5">
                {mainNavItems.map((item) => (
                  <MobileNavLink key={item.href} item={item} onClick={closeMobileMenu} />
                ))}
              </nav>

              {/* Secondary Links */}
              <div className="mt-6 pt-6 border-t border-white/[0.06] space-y-0.5">
                <p className="px-3 mb-2 text-[10px] uppercase tracking-wider text-muted-foreground/40 font-medium">
                  Resources
                </p>
                {secondaryItems.map((item) => (
                  <MobileNavLink key={item.href} item={item} onClick={closeMobileMenu} />
                ))}
                <button
                  onClick={() => {
                    closeMobileMenu()
                    setTimeout(openModal, 150)
                  }}
                  className="flex items-center gap-3 w-full px-3 py-2.5 rounded-lg text-sm text-muted-foreground/70 hover:text-foreground hover:bg-white/[0.03] transition-all duration-150"
                >
                  <MessageCircle className="h-4 w-4" />
                  <span>Contact Us</span>
                </button>
              </div>

              {/* Admin Links (conditional) */}
              {isAdmin && (
                <div className="mt-6 pt-6 border-t border-white/[0.06] space-y-0.5">
                  <p className="px-3 mb-2 text-[10px] uppercase tracking-wider text-muted-foreground/40 font-medium">
                    Admin
                  </p>
                  {adminItems.map((item) => (
                    <MobileNavLink key={item.href} item={item} onClick={closeMobileMenu} />
                  ))}
                </div>
              )}

              {/* Sign Out */}
              <div className="mt-6 pt-6 border-t border-white/[0.06]">
                <button
                  onClick={signOut}
                  className="flex items-center gap-3 w-full px-3 py-2.5 rounded-lg text-sm text-muted-foreground/70 hover:text-destructive hover:bg-destructive/5 transition-all duration-150"
                >
                  <LogOut className="h-4 w-4" />
                  <span>Sign out</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Contact Us Modal */}
      {modalState !== 'closed' && (
        <div
          className={cn(
            'fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm',
            modalState === 'open' ? 'modal-backdrop-enter' : 'modal-backdrop-exit'
          )}
          onClick={closeModal}
        >
          <div
            className={cn(
              'bg-card border border-border/50 rounded-lg p-6 w-full max-w-sm mx-4',
              'shadow-2xl shadow-black/50',
              modalState === 'open' ? 'modal-content-enter' : 'modal-content-exit'
            )}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-lg font-medium text-foreground">Contact Us</h2>
              <button
                onClick={closeModal}
                className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-secondary transition-all"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-5">
              <div>
                <p className="text-sm text-muted-foreground">
                  For any support inquiries email
                </p>
                <a
                  href="mailto:support@theseus.us"
                  className="text-sm font-medium text-primary hover:underline"
                >
                  support@theseus.us
                </a>
              </div>

              <div>
                <p className="text-sm text-muted-foreground">
                  For any sales-related inquiries email
                </p>
                <a
                  href="mailto:sales@theseus.us"
                  className="text-sm font-medium text-primary hover:underline"
                >
                  sales@theseus.us
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
